/**
 * The hub's Release tab (docs/ux/release.md): the finalisation checklist on
 * the left, the release pack on the right (Build as a polled job, the
 * immutable versions with their files, the engine's Release sentence). The
 * header keeps Release, Raise invoice and Close (D-0041). Nothing here is
 * edited; the one write is a job submission.
 */

import { statusLabel } from '@beam/core/status';
import { CheckIcon, CircleDashedIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { say } from '@/app/announcer';
import type { ChecklistItem, MatterDetail, ReleaseVersion, ReleaseView } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { StateChip } from '@/components/matter/StateChip';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { longDate, timeOfDay } from '@/lib/format';
import { Panel } from '../panels';
import { BUILD_WORD, NOT_DECIDED_SENTENCE, PACK_SENTENCE, STATUTORY_PACK_SENTENCE, STATE_WORD, buildBlockedReason, failureSentence, fixLinkFor, formatBytes, releaseSentence, summaryLine } from './describe';
import { building, useBuildRelease, useRelease } from './useRelease';

const STATE_ICON = { met: CheckIcon, failed: XIcon, not_decided: CircleDashedIcon } as const;
const STATE_CLASS = { met: 'text-beam-success', failed: 'text-beam-error', not_decided: 'text-beam-warning' } as const;

function ChecklistRow({ item, matterRef, detail, phone }: { item: ChecklistItem; matterRef: string; detail: MatterDetail; phone: boolean }) {
  const Icon = STATE_ICON[item.state];
  const fix = fixLinkFor(item.key, item.state, matterRef, detail.matter.doctor.slug, detail.matter.doctor.name);
  return (
    <li data-slot="checklist-item" data-state={item.state} data-key={item.key} className="flex flex-col gap-2 border-b border-beam-line py-3 last:border-b-0 md:flex-row md:items-start md:gap-4">
      <span className={`flex w-24 shrink-0 items-center gap-1.5 text-[13px] font-medium ${STATE_CLASS[item.state]}`}>
        <Icon aria-hidden="true" className="size-4" />
        {STATE_WORD[item.state]}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium">{item.label}</span>
        <span className="text-[13px] text-beam-text-muted">{item.detail}{item.state === 'not_decided' ? ` ${NOT_DECIDED_SENTENCE}` : ''}</span>
      </span>
      {fix ? <Link to={fix.href} aria-label={fix.name} className={buttonVariants({ variant: 'outline', size: phone ? 'lg' : 'sm' }) + (phone ? ' h-11 w-full' : ' shrink-0')}>{fix.label}</Link> : null}
    </li>
  );
}

function VersionRow({ v, matterRef, releasedAt, phone }: { v: ReleaseVersion; matterRef: string; releasedAt: string | null; phone: boolean }) {
  const released = releasedAt !== null && v.builtAt <= releasedAt;
  return (
    <li data-slot="release-version" data-version={v.version} className="flex flex-col gap-2 border-t border-beam-line pt-3 first:border-t-0 first:pt-0">
      <p className="eyebrow flex flex-wrap items-center gap-2 text-beam-text-muted">
        Version {v.version}
        {released ? <StateChip tone="done" size="sm" label={`Released ${longDate(releasedAt!)}`} /> : null}
      </p>
      <p className="text-[13px]">Built {longDate(v.builtAt)} at {timeOfDay(v.builtAt)}{v.builtBy.startsWith('job:') ? '' : ` by ${v.builtBy}`}, from report version {v.workspaceVersion}.</p>
      <ul className="flex flex-col gap-2" aria-label={`Files in version ${v.version}`}>
        {v.files.map((f) => (
          <li key={f.name} className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between md:gap-3">
            <span className="min-w-0 break-words text-sm font-medium tabular">{f.name} <span className="text-xs font-normal text-beam-text-muted">{formatBytes(f.bytes)}</span></span>
            <a
              href={`/api/matters/${encodeURIComponent(matterRef)}/release/versions/${v.version}/${encodeURIComponent(f.name)}`}
              download={f.name}
              aria-label={`Download ${f.name}, ${formatBytes(f.bytes).replace('KB', 'kilobytes').replace('MB', 'megabytes')}`}
              className={buttonVariants({ variant: 'outline', size: phone ? 'lg' : 'sm' }) + (phone ? ' h-11 w-full' : ' shrink-0')}
            >
              Download
            </a>
          </li>
        ))}
      </ul>
    </li>
  );
}

function PackPanel({ view, matterRef, detail, offline, phone }: { view: ReleaseView; matterRef: string; detail: MatterDetail; offline: boolean; phone: boolean }) {
  const [refused, setRefused] = useState<string | null>(null);
  const build = useBuildRelease(matterRef, setRefused);
  const isBuilding = building(view);
  const blocked = buildBlockedReason(view, detail.matter.status, offline);
  const nextVersion = (view.versions[0]?.version ?? 0) + 1;
  const failedCode = view.build?.state === 'failed' ? view.build.jobId : null;
  const [failure, setFailure] = useState<string | null>(null);
  // The failed job's code is on the job record, one poll away; the view carries the state.
  useEffect(() => {
    if (!failedCode) { setFailure(null); return; }
    let cancelled = false;
    void fetch(`/api/jobs/${encodeURIComponent(failedCode)}`, { credentials: 'same-origin', headers: { accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((job: { error?: { code?: string } } | null) => { if (!cancelled) setFailure(failureSentence(job?.error?.code)); })
      .catch(() => { if (!cancelled) setFailure(failureSentence(undefined)); });
    return () => { cancelled = true; };
  }, [failedCode]);
  const announced = useRef<string | null>(null);
  useEffect(() => {
    const state = view.build?.state ?? null;
    if (state === announced.current) return;
    if (state === 'succeeded' && announced.current === 'running') say(`Release pack version ${view.versions[0]?.version ?? ''} built.`);
    if (state === 'failed' && (announced.current === 'running' || announced.current === 'queued')) say('The release pack build failed.');
    announced.current = state;
  }, [view.build?.state, view.versions]);
  const releasedAt = detail.matter.keyDates.released ?? null;
  const reasonId = 'release-build-reason';

  return (
    <Panel title="Release pack">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Button
            data-primary="true"
            data-slot="build-release"
            size={phone ? 'lg' : 'default'}
            className={phone ? 'h-11 w-full' : 'self-start'}
            disabled={isBuilding || blocked !== null || build.isPending}
            aria-disabled={isBuilding || blocked !== null || build.isPending}
            aria-busy={isBuilding || build.isPending}
            aria-describedby={reasonId}
            onClick={() => { setRefused(null); say('Building the release pack.'); build.mutate(); }}
          >
            {isBuilding || build.isPending ? 'Building the pack' : 'Build release pack'}
          </Button>
          <p id={reasonId} role="status" data-slot="build-reason" className="text-xs text-beam-text-muted">
            {isBuilding && view.build ? BUILD_WORD[view.build.state] : refused ? `Refused: ${refused}` : blocked ?? `Ready to build version ${nextVersion}.`}
          </p>
        </div>
        {failure && !isBuilding ? (
          <Alert role="alert" variant="destructive" data-slot="build-failed">
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>The last build failed: {failure}</span>
              {blocked === null ? <Button variant="outline" size="sm" onClick={() => { setRefused(null); build.mutate(); }}>Try again</Button> : null}
            </AlertDescription>
          </Alert>
        ) : null}
        {view.versions.length === 0 ? (
          <p className="text-[13px] text-beam-text-muted">No pack has been built for this matter{isBuilding ? ' yet' : ''}.</p>
        ) : (
          <ul className="flex flex-col gap-3" aria-label="Release pack versions">
            {view.versions.map((v) => <VersionRow key={v.version} v={v} matterRef={matterRef} releasedAt={releasedAt} phone={phone} />)}
          </ul>
        )}
        <p className="text-[13px] text-beam-text-muted">{detail.matter.reportType === 'permanent_impairment' ? STATUTORY_PACK_SENTENCE : PACK_SENTENCE}</p>
        <p data-slot="release-sentence" className="text-[13px]">{releaseSentence(detail)}</p>
      </div>
    </Panel>
  );
}

export function ReleaseTab({ matterRef, detail, offline }: { matterRef: string; detail: MatterDetail | undefined; offline: boolean }) {
  const release = useRelease(matterRef);
  const phone = useIsPhone();
  if (!detail || release.isPending) {
    return (
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
        <Skeleton className="h-80 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }
  if (release.isError) return <ErrorPanel message="The release could not be loaded." onRetry={() => void release.refetch()} offline={offline} />;
  if (release.data === null) {
    const status = detail.matter.status;
    return (
      <EmptyState
        className="mt-2"
        title="The checklist reads the report."
        description={`It opens when the report reaches 6 ${statusLabel(6)}; the case is at ${status} ${statusLabel(status)}.`}
        action={<Link to={`/matters/${encodeURIComponent(matterRef)}?tab=report`} className={buttonVariants({ variant: 'outline' })}>Open the Report tab</Link>}
      />
    );
  }
  const view = release.data;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
      <div className={phone ? 'order-2' : ''}>
        <Panel title="Finalisation checklist">
          <p data-slot="checklist-summary" className="mb-2 text-sm">{summaryLine(view.checklist.items)}</p>
          <ul className="flex flex-col" aria-label="Finalisation checklist">
            {view.checklist.items.map((item) => <ChecklistRow key={item.key} item={item} matterRef={matterRef} detail={detail} phone={phone} />)}
          </ul>
        </Panel>
      </div>
      <div className={phone ? 'order-1' : ''}>
        <PackPanel view={view} matterRef={matterRef} detail={detail} offline={offline} phone={phone} />
      </div>
    </div>
  );
}
